import { useMemo } from "react";
import { statusColorOf } from "@/lib/tokens";
import { BRAND } from "@/lib/tokens";
import { t } from "@/lib/strings";
import type { HomeCalendarItem, UUID } from "@/lib/types";

// Local YYYY-MM-DD (never toISOString — UTC shifts the day at +TZ offsets).
function dayKey(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// Monday of the current week (local).
function thisMonday(): Date {
  const now = new Date();
  const base = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  base.setDate(base.getDate() - ((base.getDay() + 6) % 7));
  return base;
}

const DOW = ["Mo", "Di", "Mi", "Do", "Fr"];

// F7: a compact one-work-week (Mon–Fri) preview of the user's dated open work.
// Opt-in (toggled on Home), so this only renders when the user enabled it.
export function HomeCalendarWidget({
  items,
  onOpen,
}: {
  items: HomeCalendarItem[];
  onOpen: (boardId: UUID, rowId: UUID) => void;
}) {
  const { days, todayKey } = useMemo(() => {
    const mon = thisMonday();
    const ds = Array.from({ length: 5 }, (_, i) => {
      const d = new Date(mon);
      d.setDate(mon.getDate() + i);
      return d;
    });
    return { days: ds, todayKey: dayKey(new Date()) };
  }, []);

  const byDay = useMemo(() => {
    const m = new Map<string, HomeCalendarItem[]>();
    for (const it of items) {
      if (!it.due) continue;
      if (!m.has(it.due)) m.set(it.due, []);
      m.get(it.due)!.push(it);
    }
    return m;
  }, [items]);

  return (
    <div
      style={{
        display: "grid",
        // minmax(0, 1fr) overrides the grid track's default min-width:auto so a long
        // (nowrap) chip title can't blow the column — and thus the whole grid — past
        // the page's maxWidth wrapper. Tracks split evenly; titles ellipsize instead.
        gridTemplateColumns: "repeat(5, minmax(0, 1fr))",
        gap: 8,
        marginBottom: 36,
      }}
    >
      {days.map((d) => {
        const key = dayKey(d);
        const isToday = key === todayKey;
        const dayItems = byDay.get(key) ?? [];
        return (
          <div
            key={key}
            style={{
              border: "1px solid var(--hairline)",
              borderRadius: 10,
              background: "var(--surface)",
              padding: 8,
              minHeight: 96,
              display: "flex",
              flexDirection: "column",
              gap: 6,
            }}
          >
            <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: "var(--ink-3)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                }}
              >
                {DOW[(d.getDay() + 6) % 7]}
              </span>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  minWidth: 20,
                  height: 20,
                  padding: "0 5px",
                  borderRadius: 999,
                  background: isToday ? BRAND.orange : "transparent",
                  color: isToday ? "#fff" : "var(--ink-2)",
                  fontSize: 12,
                  fontWeight: isToday ? 600 : 500,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {d.getDate()}
              </span>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {dayItems.map((it) => {
                const tint = statusColorOf(it.status_color);
                return (
                  <button
                    key={it.row_id}
                    onClick={() => onOpen(it.board_id, it.row_id)}
                    title={it.title}
                    className="flow-status-tint"
                    style={
                      {
                        display: "flex",
                        alignItems: "center",
                        gap: 5,
                        padding: "3px 6px",
                        border: "none",
                        borderRadius: 5,
                        // bg/fg come from .flow-status-tint so dark mode restyles them
                        // (translucent chip + bright text) instead of a light pastel island.
                        "--pill-bg": tint.bg,
                        "--pill-fg": tint.fg,
                        "--pill-dot": tint.dot,
                        cursor: "pointer",
                        textAlign: "left",
                        overflow: "hidden",
                        fontFamily: "Inter Variable, Inter, system-ui, sans-serif",
                      } as React.CSSProperties
                    }
                  >
                    <span
                      style={{
                        width: 5,
                        height: 5,
                        borderRadius: 99,
                        background: tint.dot,
                        flexShrink: 0,
                      }}
                    />
                    <span
                      style={{
                        fontSize: 11,
                        fontWeight: 500,
                        color: "inherit",
                        // min-width:0 lets the title shrink inside its flex chip so the
                        // ellipsis actually engages (a flex item won't shrink below its
                        // content otherwise).
                        minWidth: 0,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {it.title}
                    </span>
                  </button>
                );
              })}
              {dayItems.length === 0 ? (
                <span style={{ fontSize: 11, color: "var(--ink-4)" }}>{t.homeCalendarNone}</span>
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
}
